import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ConfigProvider, theme } from 'antd'
import classNames from 'classnames'
import { assetsActions, downloadAssetThunk } from 'features/assets/model/slice/assetsSlice'
import { fetchFileVersionsThunk, fetchSharedFileVersionsThunk } from 'features/file-versions/model/slice/fileVersionsSlice'
import { useUploadingFinishDetection } from 'features/files-uploading/hooks/useUploadingFinishDetection'
import { UPLOADER_LIST_TYPES, uploadFilesThunk } from 'features/files-uploading/model/slice/filesUploadingSlice'
import { useGetEnablePermission } from 'features/public-link-files/hooks/useGetEnablePermission'
import { checkAndUploadSharedFilesThunk, downloadSharedFilesArchiveThunk } from 'features/public-link-files/model/slice/publicLinkFilesSlice'
import { fetchFamilyThunk } from 'features/team/model/slice/teamSlice'
import { MemberRole } from 'features/team/model/types/members'
import { getUserRole, isAuthSelector, userFamilyIdSelector } from 'features/user/model/selectors/userSelectors'
import _ from 'lodash'
import ReactDOM from 'react-dom'
import Dropzone from 'react-dropzone'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { request } from 'shared/api/request'
import { useAppDispatch, useAppSelector } from 'shared/config/store'
import { useDocumentKeyDown } from 'shared/hooks/useDocumentKeydown'
import { Button, FileOutlined, HistoryOutlined, InfoCircleOutlined, MessageOutlined, Tabs } from 'shared/ui'
import { hasAccess } from 'shared/utils/access'
import { styled } from 'styled-components'

import { mapFileItemsToTableRow } from '../../../shared/utils/filesUtils'
import { DropzoneArea } from '../../dropzone/DropzoneArea'
import {
  getFileVersionsItemsSelector,
} from '../../file-versions/model/selectors/fileVersionsSelectors'
import {
  getCreatingNewAnnotationModeSelector,
  getFilesPreviewActiveTabSelector,
  getFilesPreviewAnnotationsGeometryByOffsetSelector,
  getFilesPreviewCommentsSelector,
  getFilesPreviewErrorSelector,
  getFilesPreviewIsCommentsLoadingSelector,
  getFilesPreviewIsControlPanelVisibleSelector,
  getFilesPreviewItemsSelector,
  getFilesPreviewLoadingMoreSelector,
  getFilesPreviewLoadingSelector,
  getFilesPreviewModalTypeSelector,
  getFilesPreviewOverlayImageDataSelector,
  getFilesPreviewWithPreventedClosingSelector,
  getIsCreatingNewAnnotationSelector,
  getIsShowAnnotationLayerSelector,
  getIsShowAnnotationMarkersLayerSelector,
  getNewAnnotationSelector,
  getPlayerCurrentTimeSelector,
  getPlayerIsPlayingSelector,
  getSelectedAnnotationSelector
} from '../model/selectors/filesPreviewSelectors'
import {
  createCommentThunk,
  deleteCommentThunk,
  fetchFileDataThunk,
  filesPreviewActions,
  handleNextPhotoThunk,
  handlePrevPhotoThunk,
  openFilesPreviewFromParamsThunk, TabsNames,
  updateCommentStatusThunk,
  updateCommentThunk
} from '../model/slice/filesPreviewSlice'
import { FilesPreviewActiveTab } from '../model/types/filesPreviewTypes'
import { getCurrentItemIndexByItemId } from '../utils/filesPreviewUtils'
import { clearSearchResults, setSearchInputValue } from '../../common/model/slice/searchSlice'
import { disable as disableMultiSelect, enable as enableMultiSelect } from '../../common/model/slice/multiSelectSlice'
import { boardsActions } from '../../boards/model/slice/boardsSlice'

import { AnnotateButton } from './AnnotateButton'
import { RectangleIcon } from './AnnotationIcons'
import { AudioViewer } from './AudioViewer'
import { CommentsTabContent } from './CommentsTabContent'
import { FileConfirmationViewer } from './FileConfirmationViewer'
import { FilesPreviewHeader } from './FilesPreviewHeader'
import { ImageViewer } from './ImageViewer'
import { InfoTabContent } from './InfoTabContent'
import { PdfViewer } from './PdfViewer'
import { Resizer } from './Resizer'
import {
  SArrowBox,
  SArrowContainer,
  SArrowIcon,
  SContentWrapper,
  SFilePreview,
  SFilesPreviewModalBox,
  SHiddenImg,
  SMainContent,
  SOverlay,
  StyledSpinner
} from './styles'
import { ANNOTATION_MODE, CREATING_NEW_ANNOTATION_MODE } from './utils/annotationUtils'
import { VersionsTabContent } from './VersionsTabContent'
import { VideoViewer } from './VideoViewer'
import { AiAssistantInfoSidebar } from './AiAssistantInfoSidebar'

// Добавим интерфейсы для типизации
interface FileItem {
  id: string;
  name: string;
  node_id?: string;
  file_info: {
    type: string;
    mime: string;
  };
  _links: {
    self: { href: string };
    content?: { href: string };
    node?: { href: string };
  };
  _embedded?: {
    previews?: {
      url_template: string;
      jwt: string;
      sizes?: {
        preview?: {
          type: string;
        }
      }
    }
  }
}

interface FileData {
  _links: {
    self: { href: string };
    content: { href: string };
  }
}

interface ContentData {
  _links: {
    data: { href: string };
  }
}

const ANNOTATION_SUPPORTED_FILE_TYPES = ['image', 'video']

const getPreviewLink = (item: FileItem): string => {
  if (!item?._embedded?.previews?.sizes?.preview) {
    return ''
  }

  const urlTemplate = _.template(item._embedded.previews.url_template, { interpolate: /\{(.+?)\}/g })
  const url = urlTemplate({ size_name: 'preview' }) + `&jwt=${item._embedded.previews.jwt}`

  return url
}

const hasPdfPreview = (item: FileItem): boolean => {
  return !!item?._embedded?.previews?.sizes?.preview && item?._embedded?.previews?.sizes?.preview?.type === 'pdf'
}

const annotationModes = [
  {
    type: ANNOTATION_MODE.RECTANGLE,
    title: 'Rectangle Annotation',
    icon: <RectangleIcon />
  },
  // {
  //   type: ANNOTATION_MODE.POINT,
  //   title: 'Point Annotation',
  //   icon: <PointIcon />
  // },
  // Можно добавить другие режимы, если нужно
]

const filterObject = (obj: Record<string, any>) => {
  const newObj = {}

  Object.keys(obj).forEach((key) => {
    if (obj[key] !== undefined) {
      newObj[key] = obj[key]
    }
  })

  return newObj
}

export const FilesPreviewModal = () => {
  const ASSET_DOWNLOAD_TIMEOUT = 60000

  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  const isSomeSpecialModalOpened = false
  const items = useAppSelector(getFilesPreviewItemsSelector) as FileItem[]
  const totalItemsCount = items.length
  const currentIndex = getCurrentItemIndexByItemId(items, searchParams.get('prv') || '')
  const isModalOpened = !!searchParams.get('prv')
  const isLoading = useAppSelector(getFilesPreviewLoadingSelector)
  const isLoadingMore = useAppSelector(getFilesPreviewLoadingMoreSelector)
  const hasError = useAppSelector(getFilesPreviewErrorSelector)
  const isPublicLink = window.location.pathname.includes('/links/fs/')
  const isAiAssistant = window.location.pathname.includes('ai-assistant')
  const userRole = useAppSelector(getUserRole)
  const comments = useAppSelector(getFilesPreviewCommentsSelector)
  const activeTab = useAppSelector(getFilesPreviewActiveTabSelector)
  const isCommentsLoading = useAppSelector(getFilesPreviewIsCommentsLoadingSelector)
  // new annotation state variables
  const isShowAnnotationLayer = useAppSelector(getIsShowAnnotationLayerSelector)
  const isShowAnnotationMarkersLayer = useAppSelector(getIsShowAnnotationMarkersLayerSelector)
  const isCreatingNewAnnotation = useAppSelector(getIsCreatingNewAnnotationSelector)
  const creatingNewAnnotationMode = useAppSelector(getCreatingNewAnnotationModeSelector)
  const isAuth = useAppSelector(isAuthSelector)
  const selectedAnnotation = useAppSelector(getSelectedAnnotationSelector)
  const newAnnotation = useAppSelector(getNewAnnotationSelector)
  const userName = useAppSelector(state => state.user.user?.name)
  const withPreventedClosing = useAppSelector(getFilesPreviewWithPreventedClosingSelector)
  const overlayImageData = useAppSelector(getFilesPreviewOverlayImageDataSelector)
  const modalType = useAppSelector(getFilesPreviewModalTypeSelector)

  const { isDownloadEnabled, isFsNodeVersionsEnabled, isCreateCommentsEnabled, isUploadEnabled, isEditContentEnabled } = useGetEnablePermission()
  const isUploadOrEditEnabled = isUploadEnabled || isEditContentEnabled

  const currentTime = useAppSelector(getPlayerCurrentTimeSelector)
  const isAssetPlaying = useAppSelector(getPlayerIsPlayingSelector)
  // for kanva annotations
  const annotationsGeometry = useAppSelector(getFilesPreviewAnnotationsGeometryByOffsetSelector(currentTime))
  const [isDropzoneAreaVisible, setDropzoneAreaVisible] = useState(false)

  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [coverImageUrl, setCoverImageUrl] = useState('')
  const [areControlsVisible, setAreControlsVisible] = useState(true)
  const [isAnyCommentFocused, setIsAnyCommentFocused] = useState(false)
  const [isVolumeHovered, setIsVolumeHovered] = useState(false)
  const currentItem = items[currentIndex] as any
  const isAnnotationControlPanelVisible = useAppSelector(getFilesPreviewIsControlPanelVisibleSelector)
  const isAiFile = currentItem?.file_info?.mime === 'application/vnd.adobe.illustrator'


  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const currentItemIdRef = useRef<string | null>(null)
  const currentItemPreviewUrlRef = useRef<string | null>(null)
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isRestoringInProgressRef = useRef(false)

  const rightPanelWidthRef = useRef<number>(220)

  const modalRootRef = useRef<HTMLDivElement>(null)
  const versions = useAppSelector(getFileVersionsItemsSelector)
  const memoizedVersions = useMemo(() => mapFileItemsToTableRow(versions), [versions])
  const userId = useAppSelector(userFamilyIdSelector)

  // Helper function to extract properties from version object
  const extractVersionProperties = (version: any) => {
    const {
      _links = {},
      _embedded = {},
      access = {},
      author_id = {},
      checksum = {},
      created = {},
      meta_data = {},
      size = {},
      thumbnailLink = {}
    } = version
    return { _links, _embedded, access, author_id, checksum, created, meta_data, size, thumbnailLink }
  }

  useEffect(() => {
    const actualVersion = memoizedVersions[0]
    if (actualVersion && memoizedVersions.length > 1 && currentItem?._embedded?.previews?.url_template !== actualVersion?._embedded?.previews?.url_template) {
      const versionProperties = extractVersionProperties(actualVersion)
      const updatedAsset = { ...currentItem, ...versionProperties }

      dispatch(filesPreviewActions.updateItem(updatedAsset))
      dispatch(assetsActions.updateAsset(updatedAsset))
    }
  }, [memoizedVersions])

  const isFileSupportAnnotations = (item: any) => {
    const fileType = item.file_info.type
    const hasPreview = item?._embedded?.previews?.sizes?.preview?.type
    return hasPreview && ANNOTATION_SUPPORTED_FILE_TYPES.includes(fileType)
  }

  useEffect(() => {
    const fileId = searchParams.get('prv')
    const self = searchParams.get('self')

    if (isAiAssistant) {
      setSearchParams({})

      return
    }

    if (fileId) {
      dispatch(openFilesPreviewFromParamsThunk({ fileId, isPublicLink, self }))
    }
  }, [])

  useEffect(() => {
    if (modalRootRef.current && isModalOpened) {
      const savedWidth = localStorage.getItem('rightPanelWidth')

      if (savedWidth) {
        rightPanelWidthRef.current = Number(savedWidth)
        modalRootRef.current.style.setProperty('--right-panel-width', `${rightPanelWidthRef.current}px`)
      }
    }
  }, [dispatch, isModalOpened])

  const handleResize = useCallback((newWidth: number) => {
    rightPanelWidthRef.current = newWidth

    if (modalRootRef.current) {
      modalRootRef.current.style.setProperty('--right-panel-width', `${newWidth}px`)
    }

    try {
      localStorage.setItem('rightPanelWidth', String(newWidth))
    } catch (e) {
      console.error('Failed to save panel width to localStorage:', e)
    }
  }, [])

  const handleMouseMove = () => {
    setAreControlsVisible(true)

    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current)
    }
    controlsTimeoutRef.current = setTimeout(() => {
      setAreControlsVisible(false)
    }, 3000)
  }

  useEffect(() => {
    dispatch(fetchFamilyThunk())

    return () => {
      dispatch(filesPreviewActions.resetState())
    }
  }, [])
  
  const handleFinishLoadingCallback = useCallback(() => {
    if (currentItem?.id) {
      dispatch(filesPreviewActions.setActiveTab(TabsNames.VERSIONS))

      if (isPublicLink) {
        dispatch(fetchSharedFileVersionsThunk({ nodeId: currentItem.id }))
      } else {
        dispatch(fetchFileVersionsThunk({ nodeId: currentItem.id }))
      }

      // Обновляем данные файла после загрузки новой версии
      if (isRestoringInProgressRef.current) {
        dispatch(filesPreviewActions.clearOverlayImageData())
        isRestoringInProgressRef.current = false
      }
    }
  }, [currentItem?.id, dispatch, isPublicLink])

  useUploadingFinishDetection(handleFinishLoadingCallback)

  const handleFinishLoading = useCallback(() => {
    dispatch(filesPreviewActions.setLoading(false))
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current)
      loadingTimeoutRef.current = null
    }
  }, [dispatch])

  const handleNext = () => {
    if (currentIndex >= totalItemsCount - 1 || !isModalOpened || isSomeSpecialModalOpened || isCreatingNewAnnotation || !!overlayImageData?.versionId) {
      return
    }

    dispatch(handleNextPhotoThunk({
      currentIndex, callback: (nextItemId: string) => {
        navigate(`?prv=${nextItemId}`)
      }
    }))
  }

  const handlePrev = () => {
    if (currentIndex <= 0 || !isModalOpened || isSomeSpecialModalOpened || isCreatingNewAnnotation || !!overlayImageData?.versionId) {
      return
    }

    dispatch(handlePrevPhotoThunk({
      currentIndex, callback: (prevItemId: string) => {
        navigate(`?prv=${prevItemId}`)
      }
    }))
  }

  const handleError = () => {
    dispatch(filesPreviewActions.setLoading(false))
    dispatch(filesPreviewActions.setError(true))
  }

  const handleDownload = () => {
    isPublicLink ? dispatch(downloadSharedFilesArchiveThunk({ items: [currentItem] })) : dispatch(downloadAssetThunk({ id: currentItem.id }))
  }

  const handleClose = () => {
    if (isSomeSpecialModalOpened || withPreventedClosing) {
      return
    }

    setSearchParams({})
    dispatch(filesPreviewActions.resetState())
  }

  const handleVolumeHover = (isHovered: boolean) => {
    setIsVolumeHovered(isHovered)
  }

  useEffect(() => {
    if (currentIndex + 1 >= totalItemsCount) {
      dispatch(filesPreviewActions.setCurrentItemIndex(totalItemsCount - 1))
    }

    if (totalItemsCount === 0) {
      dispatch(filesPreviewActions.resetState())
    }
  }, [currentIndex, totalItemsCount])

  useEffect(() => {
    if (!isModalOpened) {
      dispatch(filesPreviewActions.resetState())
      dispatch(enableMultiSelect())
    } else {
      dispatch(clearSearchResults())
      dispatch(setSearchInputValue(''))
      dispatch(disableMultiSelect())
      dispatch(assetsActions.unselectAll())
      dispatch(boardsActions.unselectAll())
    }

    return () => {
      dispatch(enableMultiSelect())
    }
  }, [dispatch, isModalOpened])

  useEffect(() => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current)
    }

    setPreviewUrl(null)

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }

    if (!currentItem?.id) {
      return
    }
    if (isPublicLink) {
      if(currentItem.type === 'file'){
        if (currentItem._links?.node?.href) {
          const urlTemplate = _.template(currentItem._links.node.href, { interpolate: /\{(.+?)\}/g })
          const nodeLink = urlTemplate({ node_id: currentItem.node_id })
          navigate(`?prv=${currentItem.id}&pl=1&self=${nodeLink}`, { replace: true })
        } else {
          navigate(`?prv=${currentItem.id}&pl=1&self=${currentItem._links.self.href}`, { replace: true })
        }
      } else {
        navigate(`?prv=${currentItem.id}&pl=1&self=${currentItem._links.self.href}`, { replace: true })
      }
    }

    const fetchFileData = async () => {
      if (!isModalOpened || !currentItem) {
        return
      }

      dispatch(filesPreviewActions.setLoading(true))
      dispatch(filesPreviewActions.setError(false))
      setCoverImageUrl('')

      const type = currentItem?.file_info?.type
      const mime = currentItem?.file_info?.mime

      const getDataLink = (currentItem: any) => {
        return currentItem?._links?.self?.href || `/api/2/users/${userId}/fs/root/nodes/${currentItem.id}/`
      }

      const isExternalLink = (currentItem: any) => {
        return !!currentItem?._links?.self?.href
      }

      const fetchContentLink = async (currentItem: any, signal: AbortSignal) => {
        try {
          const dataLink = getDataLink(currentItem)
          const isLinkExternal = isExternalLink(currentItem)

          const fileData = await request('GET', dataLink, {}, isLinkExternal ? { host: null, signal } : { signal }) as FileData
          return  fileData?._links?.content?.href

        } catch (error) {
          console.error('Error fetching content link:', error)
          return ''
        }
      }


      try {
        abortControllerRef.current = new AbortController()
        const { signal } = abortControllerRef.current

        // if (user) {
        dispatch(fetchFileDataThunk({ node: currentItem, signal, isPublicLink }))
        // dispatch(fetchCommentsThunk({ node: currentItem, signal, isPublicLink }))
        // }
        if (mime === 'application/pdf') {
          const contentLink = await fetchContentLink(currentItem, signal)

          if (contentLink) {
            const content = await request('GET', contentLink, {}, { host: null, signal }) as ContentData
            setPreviewUrl(encodeURIComponent(content._links.data.href))
          }
        } else if (hasPdfPreview(currentItem)) {
          setPreviewUrl(encodeURIComponent(getPreviewLink(currentItem)))
        } else if (type === 'video') {
          if (isPublicLink) {
            if (currentItem?._links?.content?.href) {
              const content = await request('GET', currentItem._links.content.href, {}, { host: null, signal }) as ContentData

              setPreviewUrl(content._links.data.href)

              return
            }

            const urlTemplate = _.template(currentItem?._links?.node?.href, { interpolate: /\{(.+?)\}/g })
            const nodeLink = urlTemplate({ node_id: currentItem.node_id })

            const nodeHrefResponse = await request("GET", nodeLink, {}, { host: null }) as FileData

            const content = await request('GET', nodeHrefResponse._links.content.href, {}, { host: null, signal }) as ContentData

            setPreviewUrl(content._links.data.href)
          } else {
            const contentLink = await fetchContentLink(currentItem, signal)

            if (contentLink) {
              const content = await request('GET', contentLink, {}, { host: null, signal }) as ContentData
              setPreviewUrl(content._links.data.href)
            }
          }
        } else if (type === 'audio') {
          let content = null
          const contentLink = await fetchContentLink(currentItem, signal)

          if (contentLink) {
            content = await request('GET', contentLink, {}, { host: null, signal }) as ContentData
          }

          const coverUrl = getPreviewLink(currentItem)

          try {
            await request('GET', coverUrl, {}, { host: null, signal })
            setCoverImageUrl(coverUrl)
          } catch (error) {
            // Silent fail for cover image
          }
          if(!content) return
          setPreviewUrl(content._links.data.href)
        } else if (type === 'image' || isAiFile) {
          const url = getPreviewLink(currentItem)
          if (url === currentItemPreviewUrlRef.current) {
            dispatch(filesPreviewActions.setLoading(false))
          }

          currentItemPreviewUrlRef.current = url
          setPreviewUrl(url)
        } else {
          throw new Error('There is no file preview')
        }

        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current)
        }

        if (currentItemIdRef.current !== currentItem.id) {
          loadingTimeoutRef.current = setTimeout(() => {
            if(isLoading && !hasError) {
              dispatch(filesPreviewActions.setLoading(false))
              dispatch(filesPreviewActions.setError(true))
            }
          }, ASSET_DOWNLOAD_TIMEOUT)

          currentItemIdRef.current = currentItem.id
        }
      } catch (error: any) {
        console.error('Error fetching file data:', error)
        if (error?.name === 'AbortError') {
          return
        }
        dispatch(filesPreviewActions.setLoading(false))
        dispatch(filesPreviewActions.setError(true))
      }
    }

    if (currentItem) {
      fetchFileData()
    }
  }, [currentItem?.id, isModalOpened, currentItem?._embedded?.previews?.url_template])

  const handleCloseAnnotationMode = () => {
    dispatch(filesPreviewActions.setIsShowAnnotationLayer(false))
    dispatch(filesPreviewActions.setIsShowAnnotationMarkersLayer(false))
    dispatch(filesPreviewActions.setIsCreatingNewAnnotation(false))
    dispatch(filesPreviewActions.setCreatingNewAnnotationMode(null))
    dispatch(filesPreviewActions.setSelectedAnnotation(null))
    dispatch(filesPreviewActions.setNewAnnotation(null))
  }

  useDocumentKeyDown(37, handlePrev)
  useDocumentKeyDown(39, handleNext)
  useDocumentKeyDown(27, () => {
    if (isAnyCommentFocused) {
      return
    }
    if (overlayImageData.url && overlayImageData.url !== '') {
      dispatch(filesPreviewActions.clearOverlayImageData())
      return
    }
    if(isCreatingNewAnnotation) {
      handleCloseAnnotationMode()
      return
    }
    handleClose()
  })

  const handleTabChange = (key: FilesPreviewActiveTab) => {
    dispatch(filesPreviewActions.setActiveTab(key))
  }

  const handleAnnotationButtonClick = () => {
    if(!isCreatingNewAnnotation) {
      dispatch(filesPreviewActions.setIsShowAnnotationLayer(true))
      dispatch(filesPreviewActions.setIsShowAnnotationMarkersLayer(true))
      dispatch(filesPreviewActions.setIsCreatingNewAnnotation(true))
      dispatch(filesPreviewActions.setCreatingNewAnnotationMode(CREATING_NEW_ANNOTATION_MODE.RECTANGLE))
    } else {
      dispatch(filesPreviewActions.setIsShowAnnotationLayer(false))
      dispatch(filesPreviewActions.setIsShowAnnotationMarkersLayer(true))
      dispatch(filesPreviewActions.setIsCreatingNewAnnotation(false))
      dispatch(filesPreviewActions.setCreatingNewAnnotationMode(null))
    }
  }

  const handleAnnotationModeSelect = (type: CREATING_NEW_ANNOTATION_MODE) => {
    dispatch(filesPreviewActions.setCreatingNewAnnotationMode(type))
  }

  // Мемоизация URL для PDF
  const pdfViewerSrc = useMemo(() => {
    if (!previewUrl) return ''
    return `/pdf-viewer/web/viewer.html?file=${previewUrl}`
  }, [previewUrl])

  const isPdfPreview = useMemo(() => {
    return !!currentItem && !!previewUrl && !hasError &&
      (currentItem?.file_info?.mime === 'application/pdf' || hasPdfPreview(currentItem))
  }, [currentItem, previewUrl, hasError])

  const resizerModeType = isPdfPreview ? 'thin' : 'normal'

  if (!currentItem) {
    return null
  }

  const prevImageUrl = items[currentIndex - 1]?.file_info?.type === 'image' ? getPreviewLink(items[currentIndex - 1]) : ''
  const nextImageUrl = items[currentIndex + 1]?.file_info?.type === 'image' ? getPreviewLink(items[currentIndex + 1]) : ''

  const checkAccessVersion = () => {
    return isPublicLink ? isFsNodeVersionsEnabled : hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER])
  }

  const rightPanelTabs = [
    {
      key: TabsNames.INFO,
      label: '',
      icon: <InfoCircleOutlined style={{ fontSize: 24, margin: '0 6px' }}  />,
      children: <InfoTabContent currentIndex={currentIndex} />
    },
    {
      key: TabsNames.COMMENTS,
      label: '',
      icon: <MessageOutlined style={{ fontSize: 24, margin: '0 6px' }} />,
      children: <CommentsTabContent onAnyFocusChange={setIsAnyCommentFocused}
        currentIndex={currentIndex}
      />
    },
    checkAccessVersion() && {
      key: TabsNames.VERSIONS,
      label: '',
      icon: <HistoryOutlined style={{ fontSize: 24, margin: '0 6px' }} />,
      children: <VersionsTabContent currentIndex={currentIndex} />
    },
  ].filter(Boolean)

  const handleAnnotationSubmit = (annotation: any) => {
    const { data, type } = annotation
    const message = data.message || ''
    const offset = annotation.offset || 0
    const coordinates = annotation.coordinates
    const commentData = {
      nodeId: currentItem.id,
      message,
      mentions: data.mentions,
      parent_id: null,
      annotation: {
        type,
        coordinates,
        offset
      },
    }

    dispatch(createCommentThunk(commentData))
      .then(() => {
        console.log('comment created')
      }).catch((error) => {
        console.log('error', error)
      })
  }

  const handleAnnotationDelete = (annotation: any) => {
    const { data: { id: commentId } } = annotation
    dispatch(deleteCommentThunk({
      nodeId: currentItem.id,
      commentId,
    }))
  }

  const handleAnnotationEdit = (annotation: any) => {
    const { data:{ id, message, mentions } } = annotation
    dispatch(updateCommentThunk({
      nodeId: currentItem.id,
      commentId: id,
      message: message || '',
      mentions,
    }))
  }

  const handleAnnotationResolve = (annotation: any) => {
    const { data: { id: commentId } } = annotation
    const currentStatus = annotation.data.status || 'open'
    const newStatus = currentStatus === 'open' ? 'resolved' : 'open'

    dispatch(updateCommentStatusThunk({
      nodeId: currentItem.id,
      commentId,
      status: newStatus
    }))
  }

  const handleDrop = (acceptedFiles: File[]) => {
    setDropzoneAreaVisible(false)
    if (!currentItem?.id || acceptedFiles.length === 0) {
      return
    }

    // Проверяем разрешение на загрузку
    if (!isUploadOrEditEnabled) return

    isRestoringInProgressRef.current = true

    if (isPublicLink) {
      dispatch(checkAndUploadSharedFilesThunk({
        files: acceptedFiles as any,
        modificators: [{ target_node_id: currentItem.id }],
        uploadingSourceType: UPLOADER_LIST_TYPES.VERSIONS,
      }))
    } else {
      dispatch(uploadFilesThunk({
        files: acceptedFiles as any,
        symlinkParentId: undefined,
        modificators: [{ target_node_id: currentItem.linked_node_id || currentItem.id }],
        uploadingSourceType: UPLOADER_LIST_TYPES.VERSIONS
      }))
    }
  }

  const handleDragOver = () => {
    if(!isUploadOrEditEnabled) return
    setDropzoneAreaVisible(true)
  }

  const handleDragLeave = () => {
    setDropzoneAreaVisible(false)
  }

  const content = overlayImageData.url && overlayImageData.url !== '' ? (

    <SContentWrapper
      $isPublicLink={isPublicLink || modalType === 'ai-assistant-generated' || modalType === 'ai-assistant-user'}
    >
      <FileConfirmationViewer />
    </SContentWrapper>
  ) : (
    <Dropzone
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      {({ getRootProps }) => (
        <SContentWrapper
          {...getRootProps()}
          onMouseMove={handleMouseMove}
          $isPublicLink={isPublicLink || modalType === 'ai-assistant-generated' || modalType === 'ai-assistant-user'}
        >
          {!isCreatingNewAnnotation && !selectedAnnotation && (
            <SArrowContainer className={classNames({ hidden: !areControlsVisible })}>
              {
                currentIndex !== 0 && (
                  <SArrowBox
                    onClick={handlePrev}
                  >
                    <SArrowIcon />
                  </SArrowBox>
                )
              }
            </SArrowContainer>
          )
          }

          {
            !!currentItem && (currentItem?.file_info?.type === 'image' || isAiFile) && !!previewUrl && !hasError && (
              <ImageViewer
                isShowAnnotationLayer={isShowAnnotationLayer}
                isShowAnnotationMarkers={isShowAnnotationMarkersLayer}
                isCreatingNewAnnotation={isCreatingNewAnnotation}
                creatingNewAnnotationMode={creatingNewAnnotationMode}
                selectedAnnotation={selectedAnnotation}
                newAnnotation={newAnnotation}
                userName={userName}
                isCommentsLoading={isCommentsLoading}
                annotations={annotationsGeometry}
                previewUrl={previewUrl}
                handleFinishLoading={handleFinishLoading}
                handleError={handleError}
                isLoading={isLoading}
                areControlsVisible={areControlsVisible}
                onAnnotationSubmit={handleAnnotationSubmit}
                onAnnotationDelete={handleAnnotationDelete}
                onAnnotationEdit={handleAnnotationEdit}
                onAnnotationResolve={handleAnnotationResolve}
              />
            )
          }

          {
            !!currentItem && currentItem?.file_info?.type === 'video' && !!previewUrl && !hasError && (
              <VideoViewer
                isShowAnnotationLayer={isShowAnnotationLayer}
                isShowAnnotationMarkers={isShowAnnotationMarkersLayer}
                isCreatingNewAnnotation={isCreatingNewAnnotation}
                creatingNewAnnotationMode={creatingNewAnnotationMode}
                selectedAnnotation={selectedAnnotation}
                newAnnotation={newAnnotation}
                src={previewUrl + '#t=0.001'}
                isAssetPlaying={isAssetPlaying}
                annotations={annotationsGeometry}
                comments={comments}
                isCommentsLoading={isCommentsLoading}
                userName={userName}
                onAnnotationSubmit={handleAnnotationSubmit}
                onAnnotationDelete={handleAnnotationDelete}
                onAnnotationEdit={handleAnnotationEdit}
                onAnnotationResolve={handleAnnotationResolve}
                onCanPlay={handleFinishLoading}
                onError={handleError}
                onVolumeHover={handleVolumeHover}
              />
            )
          }

          {
            !!currentItem && currentItem?.file_info?.type === 'audio' && !!previewUrl && !hasError && (
              <AudioViewer
                src={previewUrl + '#t=0.001'}
                onCanPlay={handleFinishLoading}
                onError={handleError}
                coverImageUrl={coverImageUrl}
              />
            )
          }

          {
            isPdfPreview && (
              <PdfViewer
                onLoad={handleFinishLoading}
                src={pdfViewerSrc}
              />
            )
          }

          {
            hasError && (
              <SFilePreview>
                <SFileIcon />

                {isDownloadEnabled && (
                  <SDownloadButton
                    type="primary"
                    size='large'
                    onClick={handleDownload}
                  >
                            Download
                  </SDownloadButton>
                )}

              </SFilePreview>
            )}

          {(isLoading || isLoadingMore) && <StyledSpinner />}

          {!isCreatingNewAnnotation && !selectedAnnotation && (
            <SArrowContainer
              className={classNames('right', { hidden: !areControlsVisible })}
            >
              {
                currentIndex !== totalItemsCount - 1 && (
                  <SArrowBox
                    className="rotated"
                    onClick={handleNext}
                  >
                    <SArrowIcon />
                  </SArrowBox>
                )
              }
            </SArrowContainer>
          )
          }

          {!isLoading && isFileSupportAnnotations(currentItem) && isCreateCommentsEnabled && isAuth && modalType !== 'ai-assistant-generated' && modalType !== 'ai-assistant-user' && (
            <>
              <AnnotateButton
                isAnnotating={isCreatingNewAnnotation}
                isVisible={!isVolumeHovered}
                onClick={handleAnnotationButtonClick}
              />

              {/* <AnnotationControlPanel
            isVisible={isAnnotationControlPanelVisible && !isVolumeHovered}
            annotationType={creatingNewAnnotationMode}
            annotationModes={annotationModes}
            onAnnotationModeSelect={handleAnnotationModeSelect}
            onClose={handleCloseAnnotationMode}
          /> */}
            </>
          )}

          <StyledDropzoneArea
            visible={isDropzoneAreaVisible}
            description={'The files will be uploaded as new versions'}
          />
        </SContentWrapper>
      )}
    </Dropzone>
  )

  return ReactDOM.createPortal(
    <>
      <ConfigProvider theme={{ algorithm: theme.darkAlgorithm, token: { colorPrimary: 'var(--color-primary)' }, components: {
        Button: {
          colorPrimary: 'var(--color-primary)',
          algorithm: false,
        },
        Tabs: {
          algorithm: false,
          colorPrimary: 'var(--color-primary)',
        },
        Select: {
          algorithm: false,
          colorBgElevated: 'rgba(20, 20, 20, 1)',
          colorBgContainer: 'rgba(20, 20, 20, 1)',
        },
        Dropdown: {
          algorithm: false,
          colorBgElevated: 'rgba(20, 20, 20, 1)',
        }
      } }}
      >
        {
          isModalOpened && (
            <>
              <SOverlay />

              <SFilesPreviewModalBox  ref={modalRootRef}>
                <FilesPreviewHeader onClose={handleClose} />

                {!!prevImageUrl && <SHiddenImg src={prevImageUrl} />}

                {!!nextImageUrl && <SHiddenImg src={nextImageUrl} />}

                <SMainContent>
                  {content}

                  {modalType === 'files' && (
                    <SRightPanel>
                      <Resizer
                        onResize={handleResize}
                        modeType={resizerModeType}
                      />

                      <STabs
                        items={rightPanelTabs}
                        activeKey={activeTab}
                        onChange={handleTabChange}
                      />
                    </SRightPanel>
                  )}

                  {
                    (modalType === 'ai-assistant-generated' || modalType === 'ai-assistant-user') && (
                      <AiAssistantInfoSidebar currentIndex={currentIndex} />
                    )
                  }
                </SMainContent>
              </SFilesPreviewModalBox>
            </>
          )}

      </ConfigProvider>
    </>, document.body
  )
}

// const SMoreIcon = styled(MoreOutlined)`
//   color: var(--color-white);
//   font-size: 24px;
//   cursor: pointer;
// `

// const SStarIcon = styled(StarOutlined)`
//   color: var(--color-white);
//   font-size: 24px;
//   cursor: pointer;
// `

// const SDropdownButton = styled(DropdownButton)`
//   width: 24px;
// `


const STabs = styled(Tabs)`
  .ant-tabs-nav {
    height: 56px;
    margin-bottom: 0;
  }
  
  .ant-tabs-tab {
    margin-left: 20px;
  }
`

const SFileIcon = styled(FileOutlined)`
  font-size: 80px;
  color: var(--color-white);
`

const SDownloadButton = styled(Button)`
  margin-top: 40px;
  box-shadow: none;
`

const SRightPanel = styled.div`
  height: 100%;
  min-width: 220px;
  width: var(--right-panel-width, 360px);
  border-top: 1px solid rgba(48, 48, 48, 1);
  background-color: rgba(31, 31, 31, 1);
  position: relative;
`

const StyledDropzoneArea = styled(DropzoneArea)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
  pointer-events: none;
  border: none;
`
